import {useState} from 'react';
import {Button, Icon} from '@ezcater/tapas';
import {faTrashCan} from '@fortawesome/pro-light-svg-icons';
import useTranslation from 'next-translate/useTranslation';

import Modal from '@/components/ui/Modal';
import {OpenOrdersDocument, useConsumerCartDeleteMutation} from '@/graphql/types';
import {type DraftCartTrackingFields} from './tracking/getDraftCartTrackingFields';
import useDraftCartTracking from './tracking/useDraftCartTracking';
import {Order} from './Cart';

type DeleteCartButtonProps = {
  orderId: Order['id'];
  setKeepCartOpen: (keepCartOpen: boolean) => void;
  trackingFields: DraftCartTrackingFields;
};

const DeleteCartButton: React.FC<DeleteCartButtonProps> = ({
  orderId,
  setKeepCartOpen,
  trackingFields,
}) => {
  const {t} = useTranslation('app-bar');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {trackDeleteClick, trackDeleteConfirm, trackDeleteClosed} = useDraftCartTracking();

  const [deleteCart] = useConsumerCartDeleteMutation({
    variables: {consumerCartDeleteId: orderId},
    refetchQueries: [OpenOrdersDocument],
  });

  const openDeleteCartModal = () => {
    trackDeleteClick(trackingFields);
    setKeepCartOpen(true);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDeleteClick = async () => {
    trackDeleteConfirm(trackingFields);
    setIsSubmitting(true);
    try {
      await deleteCart();
      setKeepCartOpen(false);
      setIsDeleteModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseConfirmModal = () => {
    trackDeleteClosed(trackingFields);
    setKeepCartOpen(false);
    setIsDeleteModalOpen(false);
  };

  return (
    <Modal
      cancelLabel={t('cart.cancelDeleteDraft')}
      onOpenChange={open => !open && handleCloseConfirmModal()}
      open={isDeleteModalOpen}
      primaryAction={{
        destructive: true,
        label: t('cart.confirmDeleteDraft'),
        loading: isSubmitting,
        onClick: handleConfirmDeleteClick,
      }}
      title={t('cart.draftDeleteModalHeading')}
      trigger={
        <Button
          destructive
          variant="outlined"
          className="px-3 hover:bg-guava-400 hover:text-white"
          onClick={openDeleteCartModal}
          aria-label="delete-draft-cart"
        >
          <Icon className="no-underline" icon={faTrashCan} size="small" />
        </Button>
      }
    >
      {t('cart.draftDeleteModalBody')}
    </Modal>
  );
};

export default DeleteCartButton;
